import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, effect, inject, input, model, signal, untracked, viewChild } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { CONFIRMER } from '@shared/components/dialogs/confirm/types';
import { NOTE_COLOR_SWATCHES, htmlToSupportMarkup, pastedHtmlToSupportMarkup, renderSupportMarkup, stripSupportMarkup } from './support-markup';

/** Compacte vaste set; een emoji is gewone tekst, dus meer smaken kunnen altijd via het toetsenbord. */
const EMOJI = ['👍', '🙏', '😀', '😅', '🤔', '😢', '😮', '🎉', '✅', '❌', '⚠️', '❤️', '🚀', '👀', '💡', '🔧'] as const;

/** Commands waarvan de toolbar-knop een actief-status-highlight krijgt (queryCommandState is hiervoor betrouwbaar). */
const STATE_COMMANDS = ['bold', 'italic', 'underline', 'strikeThrough', 'insertUnorderedList'] as const;

/** Inline-opmaak die removeFormat in Chromium kan laten staan (sup, font[color], onze kleur-classes). */
function isInlineFormattingElement(element: HTMLElement): boolean {
  return (
    element.tagName === 'SUP' ||
    element.tagName === 'FONT' ||
    (element.tagName === 'SPAN' && /(?:^|\s)note-color-/.test(element.className ?? ''))
  );
}

/**
 * Herbruikbare WYSIWYG-editor voor de sumiere notitie-/reactie-opmaak
 * (vet, cursief, onderstreept, doorgehaald, superscript, kleur uit het vaste
 * 9-kleurenpalet, lijst, emoji). De opmaak wordt DIRECT toegepast en getoond
 * — de gebruiker ziet nooit de onderliggende code (wens Steven 2026-09-16);
 * de `value` is en blijft de markup-opslagvorm (round-trip via
 * renderSupportMarkup ↔ htmlToSupportMarkup), zodat weergave, e-mails en
 * bestaande data ongewijzigd blijven werken.
 *
 * Plakken: tekst plakt altijd als kale tekst (vreemde opmaak blijft buiten);
 * afbeeldingen worden hier genegeerd maar bubbelen door naar een eventuele
 * host (de reactie-composer vangt ze als bijlage).
 */
@Component({
  selector: 'support-rich-text',
  imports: [SharedModule, TranslocoModule],
  templateUrl: './support-rich-text.component.html',
  styleUrl: './support-rich-text.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportRichTextComponent {
  /** De markup-waarde (opslagvorm); two-way bruikbaar. */
  readonly value = model('');
  readonly placeholder = input('');
  /** Voor velden met een zichtbaar label erboven en dus zonder placeholder. */
  readonly ariaLabel = input('');
  readonly disabled = input(false);

  protected readonly swatches = NOTE_COLOR_SWATCHES;
  protected readonly emoji = EMOJI;
  protected readonly emojiOpen = signal(false);
  protected readonly colorOpen = signal(false);
  /** Opmaak die op de huidige selectie/cursor actief is — voor de knop-highlights. */
  protected readonly activeTools = signal<ReadonlySet<string>>(new Set());

  private readonly editorRef = viewChild<ElementRef<HTMLElement>>('editor');
  private readonly destroyRef = inject(DestroyRef);
  private readonly confirmer = inject(CONFIRMER);
  private readonly transloco = inject(TranslocoService);
  /** Laatst door de editor zelf geserialiseerde waarde — guard tegen de model-feedback-lus. */
  private lastEmitted: string | null = null;

  constructor() {
    // Externe waarde (prefill, clear, draft-herstel) → editor-inhoud verversen.
    effect(() => {
      const value = this.value();
      const editor = this.editorRef()?.nativeElement;
      untracked(() => {
        if (!editor || value === this.lastEmitted) return;
        editor.innerHTML = renderSupportMarkup(value);
        this.lastEmitted = value;
      });
    });

    // De highlight volgt de cursor/selectie; selectionchange leeft op document-niveau.
    const onSelectionChange = (): void => this.refreshActiveStates();
    document.addEventListener('selectionchange', onSelectionChange);
    this.destroyRef.onDestroy(() => document.removeEventListener('selectionchange', onSelectionChange));
  }

  /**
   * Platte tekst invoegen op de cursorpositie (bv. "Zet in tekst" in de momentdialoog).
   * Staat de cursor niet in de editor, dan komt de tekst achteraan; voor en na komt
   * een spatie waar nodig, zodat ze niet aan bestaande woorden plakt.
   */
  insertText(text: string): void {
    const editor = this.editorRef()?.nativeElement;
    if (!editor || this.disabled() || !text) return;
    const selection = window.getSelection();
    const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    if (!range || !editor.contains(range.commonAncestorContainer)) {
      const end = document.createRange();
      end.selectNodeContents(editor);
      end.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(end);
    }
    const plain = editor.textContent ?? '';
    const needsLeadingSpace = plain.length > 0 && !/\s$/.test(plain);
    this.exec('insertText', `${needsLeadingSpace ? ' ' : ''}${text} `);
  }

  protected onInput(): void {
    const editor = this.editorRef()?.nativeElement;
    if (!editor) return;
    const markup = htmlToSupportMarkup(editor);
    this.lastEmitted = markup;
    this.value.set(markup);
    this.refreshActiveStates();
  }

  /**
   * Bij het verlaten van het veld: de DOM hernormaliseren naar de canonieke
   * render van de waarde. Zo verdwijnen Chrome's rommel-structuren (geneste of
   * gesplitste tags) en toont de editor exact wat er opgeslagen wordt. Binnen
   * het component (toolbar-kliks) blijft de DOM met rust, anders zou de
   * selectie verloren gaan.
   */
  protected onFocusOut(event: FocusEvent): void {
    const host = event.currentTarget as HTMLElement;
    const next = event.relatedTarget as Node | null;
    if (next && host.contains(next)) return;
    const editor = this.editorRef()?.nativeElement;
    if (!editor) return;
    editor.innerHTML = renderSupportMarkup(this.value());
    this.refreshActiveStates();
  }

  /**
   * Plakken: HTML van het klembord wordt eerst door onze serializer gehaald,
   * zodat ondersteunde opmaak (vet, cursief, lijsten, paletkleuren enz.) zijn
   * equivalent in ons systeem krijgt en al de rest wegvalt; zonder HTML plakt
   * de kale tekst. Afbeeldingen negeren we (de host vangt ze eventueel als bijlage).
   */
  protected onPaste(event: ClipboardEvent): void {
    if (this.disabled()) {
      event.preventDefault();
      return;
    }
    const files = Array.from(event.clipboardData?.files ?? []);
    if (files.some(file => file.type.startsWith('image/'))) {
      event.preventDefault();
      return;
    }
    const html = event.clipboardData?.getData('text/html');
    if (html) {
      const markup = pastedHtmlToSupportMarkup(html);
      if (markup) {
        event.preventDefault();
        this.exec('insertHTML', renderSupportMarkup(markup));
        return;
      }
    }
    const text = event.clipboardData?.getData('text/plain');
    if (text == null) return;
    event.preventDefault();
    this.exec('insertText', text);
  }

  protected bold(): void {
    this.exec('bold');
  }

  protected italic(): void {
    this.exec('italic');
  }

  protected underline(): void {
    this.exec('underline');
  }

  protected strikethrough(): void {
    this.exec('strikeThrough');
  }

  /**
   * Superscript togglen we zelf: Chromium's queryCommandState('superscript')
   * geeft false terwijl de selectie in een <sup> staat, waardoor execCommand
   * blijft TOEVOEGEN in plaats van uitzetten — de "vast in superscript"-bug.
   * Actief = zelf detecteren; uitzetten = de geraakte <sup>'s uitpakken.
   */
  protected superscript(): void {
    if (this.disabled()) return;
    const editor = this.editorRef()?.nativeElement;
    if (!editor) return;
    editor.focus();
    const sups = this.selectionSups(editor);
    if (sups.length === 0) {
      this.exec('superscript');
      return;
    }
    // replaceWith laat de tekstnodes intact, dus de selectie blijft staan.
    sups.forEach(sup => sup.replaceWith(...Array.from(sup.childNodes)));
    this.onInput();
  }

  protected bulletList(): void {
    this.exec('insertUnorderedList');
  }

  /**
   * Opmaak wissen: mét selectie alleen de selectie terug naar kale tekst;
   * zónder selectie het hele veld, na een bevestigingsdialoog (wens Steven).
   * Zonder iets te wissen (al kale tekst) doet de knop stil niets.
   */
  protected async resetFormatting(): Promise<void> {
    if (this.disabled()) return;
    const editor = this.editorRef()?.nativeElement;
    if (!editor) return;

    const selection = document.getSelection();
    const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    const hasSelection = !!range && !range.collapsed && editor.contains(range.commonAncestorContainer);

    if (hasSelection) {
      editor.focus();
      document.execCommand('styleWithCSS', false, 'false');
      document.execCommand('removeFormat');
      // removeFormat laat sup/font/kleur-classes in Chromium nog wel eens staan; zelf uitpakken.
      this.selectionIntersecting(editor, isInlineFormattingElement).forEach(element =>
        element.replaceWith(...Array.from(element.childNodes)));
      this.onInput();
      return;
    }

    const stripped = stripSupportMarkup(this.value());
    if (stripped === this.value()) return;
    const message = this.transloco.translate<string>('support.composer.resetConfirm');
    if (!(await this.confirmer.confirm(message, { color: 'danger' }))) return;
    editor.innerHTML = renderSupportMarkup(stripped);
    this.onInput();
  }

  protected toggleEmoji(): void {
    this.colorOpen.set(false);
    this.emojiOpen.update(open => !open);
  }

  protected insertEmoji(emoji: string): void {
    this.emojiOpen.set(false);
    this.exec('insertText', emoji);
  }

  protected toggleColor(): void {
    this.emojiOpen.set(false);
    this.colorOpen.update(open => !open);
  }

  protected applyColor(hex: string): void {
    this.colorOpen.set(false);
    this.exec('foreColor', hex);
  }

  /**
   * execCommand is verouderd maar universeel gedragen en dekt exact onze set
   * (bold/italic/underline/strikeThrough/superscript/foreColor/lijst/insertText);
   * de serializer normaliseert de uitvoer naar het markup-contract.
   * styleWithCSS gaat vóór elk commando expliciet uit (document-globale
   * toestand): tags en font[color] i.p.v. style-spans, voorspelbaar voor de
   * serializer — die vangt style-spans overigens ook op.
   */
  private exec(command: string, argument?: string): void {
    if (this.disabled()) return;
    const editor = this.editorRef()?.nativeElement;
    if (!editor) return;
    editor.focus();
    document.execCommand('styleWithCSS', false, 'false');
    document.execCommand(command, false, argument);
    this.onInput();
  }

  /** Alle <sup>-elementen die de huidige selectie raakt (cursor erin of overlap ermee). */
  private selectionSups(editor: HTMLElement): HTMLElement[] {
    return this.selectionIntersecting(editor, element => element.tagName === 'SUP');
  }

  /** Elementen (volgens `matches`) die de huidige selectie raakt: als voorouder van de cursor of met echte overlap. */
  private selectionIntersecting(editor: HTMLElement, matches: (element: HTMLElement) => boolean): HTMLElement[] {
    const selection = document.getSelection();
    if (!selection || selection.rangeCount === 0) return [];
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) return [];

    const found = new Set<HTMLElement>();
    const collectAncestors = (node: Node | null): void => {
      let current = node;
      while (current && current !== editor) {
        if (current instanceof HTMLElement && matches(current)) found.add(current);
        current = current.parentNode;
      }
    };
    collectAncestors(range.startContainer);
    collectAncestors(range.endContainer);

    if (!range.collapsed) {
      editor.querySelectorAll('*').forEach(candidate => {
        if (!(candidate instanceof HTMLElement) || !matches(candidate)) return;
        const candidateRange = document.createRange();
        candidateRange.selectNodeContents(candidate);
        // Echte overlap vereist (grens-aan-grens telt niet), anders pakt een
        // selectie náást zo'n element dat element ook mee.
        const overlaps =
          range.compareBoundaryPoints(Range.END_TO_START, candidateRange) < 0 &&
          range.compareBoundaryPoints(Range.START_TO_END, candidateRange) > 0;
        if (overlaps) found.add(candidate);
      });
    }
    return [...found];
  }

  private refreshActiveStates(): void {
    const editor = this.editorRef()?.nativeElement;
    const selection = document.getSelection();
    const inside =
      !!editor && !!selection && selection.rangeCount > 0 && editor.contains(selection.getRangeAt(0).commonAncestorContainer);
    if (!inside) {
      if (this.activeTools().size > 0) this.activeTools.set(new Set());
      return;
    }

    const next = new Set<string>();
    for (const command of STATE_COMMANDS) {
      try {
        if (document.queryCommandState(command)) next.add(command);
      } catch {
        // queryCommandState kan gooien op exotische selecties; dan gewoon geen highlight.
      }
    }
    if (this.selectionSups(editor).length > 0) next.add('superscript');

    const previous = this.activeTools();
    if (previous.size !== next.size || [...next].some(command => !previous.has(command))) {
      this.activeTools.set(next);
    }
  }
}
